import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorMsg, SuccessMsg, Spinner, Logo } from '../components/UI';

// This page is loaded via the redirect from Supabase's reset email.
// Supabase appends #access_token=... to the URL. We read it and use it.
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    // Get token from URL hash
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace('#', '?'));
    const token = params.get('access_token');

    if (!token) {
      setError('Invalid or expired reset link. Please request a new one.');
      return;
    }

    setLoading(true);
    try {
      // Use the token to update password via Supabase client
      // Since we're calling the backend, we use the token as the auth header
      const res = await fetch(`${BASE}/auth/update-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ password }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to reset password.');
      }

      setSuccess('Password updated! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-green-100 rounded-full opacity-40" />
        <div className="absolute -bottom-10 -left-10 w-60 h-60 bg-amber-100 rounded-full opacity-50" />
      </div>

      <div className="w-full max-w-sm relative">
        <div className="text-center mb-8">
          <Logo size="lg" className="mx-auto mb-3" />
          <h1 className="font-serif font-bold text-3xl text-green-900">GolfBoard Scores</h1>
        </div>

        <div className="card p-6 fade-in">
          <h2 className="font-serif font-semibold text-xl text-green-900 mb-1">Set New Password</h2>
          <p className="text-sm text-gray-500 mb-5">Choose a new password for your account.</p>

          <form onSubmit={handleReset} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">New Password</label>
              <input
                type="password"
                className="input-field"
                placeholder="At least 8 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5 uppercase tracking-wide">Confirm Password</label>
              <input
                type="password"
                className="input-field"
                placeholder="Repeat your password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
              />
            </div>

            <ErrorMsg message={error} />
            <SuccessMsg message={success} />

            <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={loading}>
              {loading ? <Spinner size="sm" /> : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}